import React, { useRef, useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import BillingService from '../../../services/BillingService'
import Swal from 'sweetalert2';


const styles = {
  container: { fontFamily: "Arial, sans-serif", fontSize: "13px", margin: "20px" },
  headingBox: {
    textAlign: "center",
    fontSize: "15px",
    fontWeight: "bold",
    marginBottom: "10px",
  },
  borderBox: {
    border: "1px solid #000",
    padding: "10px",
    marginBottom: "10px",
  },
  sectionRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
  },
  section: {
    border: "1px solid #000",
    padding: "6px",
    height: "100%",
    boxSizing: "border-box",
    wordBreak: "break-word",
    overflowWrap: "break-word",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    textTransform: "uppercase",   // ✅ fixed
  },
  thTd: {
    border: "1px solid #000",
    padding: "4px",
    verticalAlign: "top",
    textTransform: "uppercase",   // ✅ added here too
  },
  thead: {
    backgroundColor: "#f0f0f0",
    textTransform: "uppercase",   // ✅ fixed
  },

  signature: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: "40px",
    textTransform: "uppercase",   // ✅ fixed
  },
  underline: {
    textDecoration: "underline",
  },

};


const PoPrintModal = ({ show, onHide, item }) => {

  const [billingAddress, setBillingAddress] = useState(null);
  const [supplier, setSupplier] = useState(null);
  const printRef = useRef();
  const numberToWordsWithPaise = (amount) => {
    const ones = [
      "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
      "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
      "Seventeen", "Eighteen", "Nineteen"
    ];
    const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

    const numToWords = (n) => {
      if (n < 20) return ones[n];
      if (n < 100) return `${tens[Math.floor(n / 10)]} ${ones[n % 10]}`.trim();
      if (n < 1000)
        return `${ones[Math.floor(n / 100)]} Hundred ${numToWords(n % 100)}`.trim();
      if (n < 100000)
        return `${numToWords(Math.floor(n / 1000))} Thousand ${numToWords(n % 1000)}`.trim();
      if (n < 10000000)
        return `${numToWords(Math.floor(n / 100000))} Lakh ${numToWords(n % 100000)}`.trim();
      return `${numToWords(Math.floor(n / 10000000))} Crore ${numToWords(n % 10000000)}`.trim();
    };

    const [rupees, paise] = amount.toFixed(2).split(".").map(Number);
    const rupeePart = rupees === 0 ? "" : `${numToWords(rupees)} Rupees`;
    const paisePart = paise === 0 ? "" : `${numToWords(paise)} Paise`;

    const combined = [rupeePart, paisePart].filter(Boolean).join(" and ");

    return combined ? `${combined} Only` : "Zero Rupees Only";
  };

  useEffect(() => {
    const fetchBillingAddress = async () => {
      try {
        const state = item?.requisitionNo?.store?.address?.state;
        if (state) {
          const res = await BillingService.activeSuppliersState(state);
          if (res.data?.length > 0) {
            setBillingAddress(res?.data[0]); // assuming first match is enough
          }
        }
      } catch (error) {
        console.error("Error fetching billing address:", error);
        Swal.fire({
          icon: 'error',
          title: 'Failed to fetch billing address',
          text: error?.response?.data?.message || 'Something went wrong. Please try again later.',
        });
      }

    };

    if (item) fetchBillingAddress();
  }, [item]);



  useEffect(() => {
    if (item?.items?.length > 0 && item.items[0]?.supplier) {
      setSupplier(item.items[0].supplier);
    }
  }, [item]);

  const handlePrint = () => {
    const printWindow = window.open("", "_blank", "width=900,height=900");

    printWindow.document.write(`
    <html>
   <head>
  <title>Print PO</title>

  <link
    rel="stylesheet"
    href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.3/dist/css/bootstrap.min.css"
  />

  <style>
    body{
      font-family:Arial,sans-serif;
      font-size:13px;
      margin:20px;
    }

    table{
      width:100%;
      border-collapse:collapse;
      text-transform:uppercase;
    }

    th,td{
      border:1px solid #000;
      padding:6px;
      vertical-align:top;
    }

    img{
      max-width:200px;
      height:auto;
      display:block;
    }

    @media print{

      .row{
        display:flex !important;
        flex-wrap:wrap !important;
        margin:0 !important;
      }

      .row > .col-12,
      .row > .col-md-6{
        width:50% !important;
        max-width:50% !important;
        flex:0 0 50% !important;
        padding:4px !important;
        box-sizing:border-box;
      }

    }
  </style>
</head>
      <body>
        ${printRef.current.innerHTML}
      </body>
    </html>
  `);

    printWindow.document.close();

    const images = printWindow.document.images;

    Promise.all(
      Array.from(images).map((img) => {
        if (img.complete) return Promise.resolve();

        return new Promise((resolve) => {
          img.onload = resolve;
          img.onerror = resolve;
        });
      })
    ).then(() => {
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    });
  };





  const formatCurrency = (value) => {
    const num = Number(value);
    return isNaN(num)
      ? "₹0.00"
      : num.toLocaleString("en-IN", {
        style: "currency",
        currency: "INR",
        minimumFractionDigits: 2,
      });
  };
// Use the selected print version's item values.
// A zero-value FOC version keeps the FOC layout.
// A version with payable item amounts shows commercial columns.
const hasPayableItems = (item?.items || []).some((row) => {
  const qty = Number(row?.approveQty) || 0;
  const rate = Number(row?.rate) || 0;

  return qty > 0 && rate > 0;
});

const isFOC =
  item?.poType === "FOC PO" && !hasPayableItems;
  const hasIGST = item?.items?.some((row) => Number(row.igst) > 0);

  let totalQty = 0;
  let totalAmount = 0;
  let totalCGST = 0;
  let totalSGST = 0;
  let totalIGST = 0;
  let totalInclGst = 0;

  const getItemRow = (row, index) => {
    const qty = Number(row.approveQty) || 0;
    const rate = Number(row.rate) || 0;
    const cgst = Number(row.cgst) || 0;
    const sgst = Number(row.sgst) || 0;
    const igst = Number(row.igst) || 0;

    const baseAmount = qty * rate;
    const gstAmount = baseAmount * ((cgst + sgst + igst) / 100);
    const amountInclGST = baseAmount + gstAmount;

    totalQty += qty;
    if (!isFOC) {
      totalAmount += baseAmount;
      totalCGST += (cgst / 100) * baseAmount;
      totalSGST += (sgst / 100) * baseAmount;
      totalIGST += (igst / 100) * baseAmount;
      totalInclGst += amountInclGST;
    }

    return (
      <tr key={index}>
        <td style={styles.thTd}>{index + 1}</td>
        <td style={styles.thTd}>{row.description}</td>
        <td style={styles.thTd}>{row.unit}</td>
        <td style={styles.thTd}>{qty}</td>
        <td style={styles.thTd}>{formatCurrency(rate)}</td>
        {!isFOC && <td style={styles.thTd}>{formatCurrency(baseAmount)}</td>}
        {!isFOC && <td style={styles.thTd}>{cgst}</td>}
        {!isFOC && <td style={styles.thTd}>{sgst}</td>}
        {hasIGST && !isFOC && <td style={styles.thTd}>{igst}</td>}
        {!isFOC && <td style={styles.thTd}>{formatCurrency(amountInclGST)}</td>}
      </tr>
    );
  };


  if (!item) return null;

  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Purchase Order</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div ref={printRef} style={styles.container}>
          {/* Company Info */}
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
            <div className="text-center">
              <img
                src="https://indigoinfra.in/img/product/product-1739471679615.jpeg"
                alt="Indigo Logo"
                className="img-fluid"
                style={{ maxWidth: "200px", height: "auto" }}
              />
            </div>

            <div className="text-md-end text-center flex-grow-1">
              <h2 className="m-0">Indigo Infraprojects Pvt. Ltd.</h2>

              <br />

              <span className="text-uppercase">
                3rd Floor, Plot #12/B, Pocket 3, Sec-17, Dwarka, New Delhi-110075
                <br />
                Ph: +91-11-45720141, 44458664
                <br />
                info@indigoinfraprojects.com
                <br />
              </span>

              <strong>CIN: U70102DL2012PTC232657</strong>
            </div>
          </div>

          {/* Heading */}
          <div style={styles.headingBox}>
            {item?.status === "Confirmed-Generated"
              ? "PURCHASE ORDER (DIGITAL COPY)"
              : item?.status === "Order-Received"
                ? "PURCHASE ORDER (ORDER RECEIVED)"
                : item?.status === "Partially-Received"
                  ? "PURCHASE ORDER (PARTIALLY RECEIVED)"
                  : item?.status === "Completed"
                    ? "PURCHASE ORDER (COMPLETED)"
                    : item?.status === "Bill-Generated"
                      ? "PURCHASE ORDER (UNDER BILLING)"
                      : item?.status === "PaymentPending"
                        ? "PURCHASE ORDER (PAYMENT PENDING)"
                        : "PURCHASE ORDER (UNDER APPROVAL)"}
          </div>


          {/* PO & Supplier Details */}
          {/* PO & Supplier Details */}
          <div style={styles.borderBox}>
            {/* First Row */}
            <div className="row g-2">
              {/* PO Details */}
              <div className="col-12 col-md-6">
                <div style={styles.section}>
                  <p className="text-uppercase mb-0">
                    <strong>PO No</strong> -{" "}
                    {new Date(item?.createdAt).getFullYear()}/
                    {String(new Date(item?.createdAt).getMonth() + 1).padStart(2, "0")}/
                    {item?.requisitionNo?.store?.name}/
                    {item?.poNumber}
                    <br />

                    <strong>Order Date:</strong>{" "}
                    {new Date(item?.createdAt).toLocaleDateString("en-IN")}
                    <br />

                    <strong>Requisition No:</strong>{" "}
                    {item?.requisitionNo?.requisitionNo}
                  </p>
                </div>
              </div>

              {/* Billing Address */}
              <div className="col-12 col-md-6">
                <div style={styles.section}>
                  <p className="text-uppercase mb-0">
                    <strong>Billing Address</strong>
                    <br />

                    Indigo Infraprojects Pvt. Ltd.
                    <br />

                    {billingAddress?.address}
                    <br />

                    <strong>GSTIN:</strong> {billingAddress?.gstNumber}
                  </p>
                </div>
              </div>
            </div>

            {/* Second Row */}
            <div className="row g-2 mt-1">
              {/* Supplier Details */}
              <div className="col-12 col-md-6">
                <div style={styles.section}>
                  <p className="text-uppercase mb-0">
                    <strong>Supplier Details</strong>
                    <br />

                    {supplier?.name}
                    <br />

                    {supplier?.address?.street}, {supplier?.address?.city},
                    <br />
                    {supplier?.address?.state} - {supplier?.address?.pincode},
                    <br />
                    {supplier?.address?.country}
                    <br />

                    <strong>Kind Attn:</strong> {supplier?.contactPerson}
                    <br />

                    <strong>Phone:</strong>{" "}
                    {Array.isArray(supplier?.mobileNo)
                      ? supplier.mobileNo.join(", ")
                      : supplier?.mobileNo}
                    <br />

                    <strong>GST:</strong> {supplier?.GSTNo}
                    <br />

                    <strong>Email:</strong>{" "}
                    {Array.isArray(supplier?.email)
                      ? supplier.email.join(", ")
                      : supplier?.email}
                  </p>
                </div>
              </div>

              {/* Shipping Address */}
              <div className="col-12 col-md-6">
                <div style={styles.section}>
                  <p className="text-uppercase mb-0">
                    <strong>Shipping Address</strong>
                    <br />

                    {item?.requisitionNo?.store?.name},
                    <br />

                    {item?.requisitionNo?.store?.address?.street}
                    <br />

                    {item?.requisitionNo?.store?.address?.city},{" "}
                    {item?.requisitionNo?.store?.address?.state} -{" "}
                    {item?.requisitionNo?.store?.address?.pincode}
                    <br />

                    {item?.requisitionNo?.store?.address?.country}
                    <br />

                    <strong>Contact Person:</strong>{" "}
                    {item?.requisitionNo?.user?.name}
                    <br />

                    <strong>Phone:</strong>{" "}
                    {item?.requisitionNo?.user?.mobileNo}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Subject */}
          <div style={{ marginBottom: "20px", textTransform: 'uppercase' }}>
            <strong>Sub: {item?.subject}</strong><br />
            <p style={{ marginTop: "10px" }}>
              Ref: By Quotation On dtd {item?.createdAt && new Date(item.createdAt).toLocaleDateString('en-GB')}
              <br />
              Dear Sir,<br />
              {item?.reference}</p>
          </div>

          {/* Table */}
          <div className="scrolltable">
            <table style={styles.table}>
              <thead style={styles.thead} className="sticky-table-header">
                <tr>
                  <th style={styles.thTd}>S. No.</th>
                  <th style={styles.thTd}>Description</th>
                  <th style={styles.thTd}>Unit</th>
                  <th style={styles.thTd}>Qty.</th>
                  <th style={styles.thTd}>Rate</th>
                  {!isFOC && <th style={styles.thTd}>Amount</th>}
                  {!isFOC && <th style={styles.thTd}>CGST (%)</th>}
                  {!isFOC && <th style={styles.thTd}>SGST (%)</th>}
                  {hasIGST && !isFOC && <th style={styles.thTd}>IGST (%)</th>}
                  {!isFOC && <th style={styles.thTd}>Amount incl. GST</th>}
                </tr>
              </thead>

              <tbody>
                {item?.items?.map(getItemRow)}

                {/* Total Qty */}
                <tr>
                  <td colSpan="3" style={styles.thTd}><strong>Total Qty</strong></td>
                  <td style={styles.thTd}><strong>{totalQty}</strong></td>
                  {!isFOC && <>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    {hasIGST && <td></td>}
                    <td></td>
                  </>}
                </tr>

                {!isFOC && (
                  <>
                    <tr>
                      <td colSpan={hasIGST ? "9" : "8"} style={styles.thTd}><strong>Total Amount</strong></td>
                      <td style={styles.thTd}>{formatCurrency(totalAmount)}</td>
                    </tr>
                    <tr>
                      <td colSpan={hasIGST ? "9" : "8"} style={styles.thTd}>CGST Total</td>
                      <td style={styles.thTd}>{formatCurrency(totalCGST)}</td>
                    </tr>
                    <tr>
                      <td colSpan={hasIGST ? "9" : "8"} style={styles.thTd}>SGST Total</td>
                      <td style={styles.thTd}>{formatCurrency(totalSGST)}</td>
                    </tr>
                    {hasIGST && (
                      <tr>
                        <td colSpan="9" style={styles.thTd}>IGST Total</td>
                        <td style={styles.thTd}>{formatCurrency(totalIGST)}</td>
                      </tr>
                    )}
                    <tr>
                      <td colSpan={hasIGST ? "9" : "8"} style={styles.thTd}>
                        <strong>Grand Total (incl. taxes & Rounded Off)</strong>
                      </td>
                      <td style={styles.thTd}>
                        <strong>{Math.round(totalInclGst).toLocaleString("en-IN")}</strong>
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={hasIGST ? "10" : "9"} style={styles.thTd}>
                        <strong>Grand Total (in words):</strong> {numberToWordsWithPaise(Math.round(totalInclGst))}
                      </td>
                    </tr>
                  </>
                )}
              </tbody>

            </table>
          </div>

          {/* Terms & Conditions */}

          {item?.termCondition?.length > 0 && (
            <div style={{ marginTop: "20px" }}>
              <p className="text-center text-md-start">
                <strong>Terms & Conditions:</strong>
              </p>
              <ol>
                {item.termCondition.map((term, index) => {
                  // remove any leading numbers like "1. ", "2. ", etc.
                  const cleanTerm = term.replace(/^\d+\.\s*/, "");
                  return <li key={index}>{cleanTerm}</li>;
                })}
              </ol>
            </div>
          )}



          {/* Signatures */}
          <div style={{ marginTop: "30px" }}>
            {/* Notice line (full width, centered) */}
            <div style={{ marginBottom: "15px", textTransform: 'upperCase' }}>
              <p className="text-center text-md-start"> <strong>
                Please sign and return back a copy of this Purchase Order as an acceptance.
              </strong>
              </p>
            </div>

            {/* Two column signature row */}
            <div style={styles.signature}>
              {/* Left (Company) */}
              <div>
                <p>
                  <br />
                  For and on behalf of<br />
                  <strong>Indigo Infraprojects Pvt. Ltd.</strong>
                </p>
                <div
                  style={{
                    width: "150px",
                    height: "100px",
                    margin: "10px 0px",
                  }}
                />
                <span>Authorised Signature</span>
              </div>

              {/* Right (Supplier) */}
              <div style={{ textAlign: "right" }}>
                <p>
                  Received and Accepted By<br />
                  For and on Behalf of<br />
                  <strong>{supplier?.name}</strong>
                </p>
                <div
                  style={{
                    width: "150px",
                    height: "100px",
                    margin: "10px 0px",
                    marginLeft: 'auto'
                  }}
                />
                <span>Authorised Signature</span>
              </div>
            </div>
          </div>


          {/* Created By */}
          <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: '10px' }}>
            CREATED BY: {item?.createdBy?.name || "N/A"}
          </div>

        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onHide}>Close</Button>
        <Button variant="primary" size="sm" onClick={handlePrint}>Print PO</Button>
      </Modal.Footer>

    </Modal >
  );
};

export default PoPrintModal;
